import React from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, typography } from '../../styles/designTokens';

/**
 * Breadcrumbs Component
 * 
 * Navigation breadcrumbs showing current location in app hierarchy.
 * Helps users understand where they are and navigate back.
 * 
 * Usage:
 * <Breadcrumbs>
 *   <Breadcrumb href="/dashboard">Dashboard</Breadcrumb>
 *   <Breadcrumb href="/team-manager">Team Manager</Breadcrumb>
 *   <Breadcrumb current>Edit User</Breadcrumb>
 * </Breadcrumbs>
 * 
 * Or with items prop:
 * <Breadcrumbs items={[
 *   { label: 'Dashboard', href: '/dashboard' },
 *   { label: 'Team Manager', href: '/team-manager' },
 *   { label: 'Edit User', current: true }
 * ]} />
 */

const Breadcrumbs = ({ children, items, className = '' }) => {
  // If items prop provided, render from array
  if (items && items.length > 0) {
    return (
      <nav
        aria-label="Breadcrumb"
        className={className}
        style={{
          padding: '3px 0 4px',
          fontSize: '12px',
        }}
      >
        <ol
          style={{
            display: 'flex',
            alignItems: 'center',
            listStyle: 'none',
            margin: 0,
            padding: 0,
            flexWrap: 'wrap'
          }}
        >
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            const isCurrent = item.current || isLast;

            return (
              <li
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {isCurrent ? (
                  <span
                    style={{
                      color: '#6b7280',
                      fontWeight: 500
                    }}
                    aria-current="page"
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    to={item.href}
                    style={{
                      color: '#9ca3af',
                      textDecoration: 'none',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#6b7280';
                      e.currentTarget.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#9ca3af';
                      e.currentTarget.style.textDecoration = 'none';
                    }}
                  >
                    {item.label}
                  </Link>
                )}

                {!isLast && (
                  <svg
                    className="w-3 h-3 mx-1.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ color: '#d1d5db' }}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }

  // Render children (composition API)
  const childrenArray = React.Children.toArray(children);

  return (
    <nav
      aria-label="Breadcrumb"
      className={className}
      style={{
        padding: '3px 0 4px',
        fontSize: '12px',
      }}
    >
      <ol
        style={{
          display: 'flex',
          alignItems: 'center',
          listStyle: 'none',
          margin: 0,
          padding: 0,
          flexWrap: 'wrap'
        }}
      >
        {childrenArray.map((child, index) => {
          const isLast = index === childrenArray.length - 1;

          return (
            <li
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {child}

              {!isLast && (
                <svg
                  className="w-3 h-3 mx-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ color: '#d1d5db' }}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

/**
 * Breadcrumb Component
 * 
 * Individual breadcrumb item
 * 
 * Usage:
 * <Breadcrumb href="/dashboard">Dashboard</Breadcrumb>
 * <Breadcrumb current>Current Page</Breadcrumb>
 */
export const Breadcrumb = ({ href, current = false, children }) => {
  if (current) {
    return (
      <span
        style={{
          color: '#6b7280',
          fontWeight: 500
        }}
        aria-current="page"
      >
        {children}
      </span>
    );
  }

  return (
    <Link
      to={href}
      style={{
        color: '#9ca3af',
        textDecoration: 'none',
        transition: 'color 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = '#6b7280';
        e.currentTarget.style.textDecoration = 'underline';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = '#9ca3af';
        e.currentTarget.style.textDecoration = 'none';
      }}
    >
      {children}
    </Link>
  );
};

export default Breadcrumbs;

